import { DeleteFilled } from "@ant-design/icons";
import { faClockRotateLeft, faCloudUploadAlt, faEye, faFileExport, faLock, faPenToSquare, faServer } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip } from "antd";
import SlideoutMenu from "components/atoms/SlideoutMenu";
import CypressTestIds from "../../../cypress/CypressTestIds";

interface IActionButtonsProps {
    dataSet: IDataSet;
    selected?: boolean;
    permission: IPermissionState;
    exportPermission?: IPermissionState;
    runningJobsCount?: number;
    onViewDataSet: (id: string) => void;
    onEditDataSet: (id: string) => void;
    onViewDataSetHistory: (id: string) => void;
    onDeleteDataSet: (s: IDataSet) => void;
    onIndexDataSet: (s: IDataSet) => void;
    onExportToFileClick?: (s: IDataSet) => void;
    onExportToSystemConnectionClick?: (s: IDataSet) => void;
}

export default function DataSetListActionButtons({
    dataSet,
    selected = false,
    permission,
    exportPermission,
    runningJobsCount,
    onViewDataSet,
    onEditDataSet,
    onViewDataSetHistory,
    onDeleteDataSet,
    onIndexDataSet,
    onExportToFileClick,
    onExportToSystemConnectionClick,
}: IActionButtonsProps) {
    const { dataSetId } = dataSet;

    return (
        <SlideoutMenu>
            {!!permission.canEdit && (
                <Tooltip title="Edit DataSet">
                    <Button
                        type="link"
                        icon={<FontAwesomeIcon icon={faPenToSquare} />}
                        disabled={!permission.canEdit}
                        onClick={(event) => {
                            event && event.stopPropagation && event.stopPropagation();
                            onEditDataSet(dataSetId);
                        }}
                        data-testid={CypressTestIds.DATASET_LIST_EDIT_BUTTON}
                    />
                </Tooltip>
            )}
            {!!permission.canView && (
                <Tooltip title="View DataSet">
                    <Button
                        type="link"
                        icon={<FontAwesomeIcon icon={faEye} />}
                        disabled={!permission.canView}
                        onClick={(event) => {
                            event && event.stopPropagation && event.stopPropagation();
                            onViewDataSet(dataSetId);
                        }}
                        data-testid={CypressTestIds.DATASET_LIST_VIEW_BUTTON}
                    />
                </Tooltip>
            )}
            {!!permission.canView && (
                <Tooltip title="DataSet Change History">
                    <Button
                        type="link"
                        icon={<FontAwesomeIcon icon={faClockRotateLeft} />}
                        disabled={!permission.canView}
                        onClick={(event) => {
                            event && event.stopPropagation && event.stopPropagation();
                            onViewDataSetHistory(dataSetId);
                        }}
                        data-testid={CypressTestIds.DATASET_LIST_HISTORY_BUTTON}
                    />
                </Tooltip>
            )}
            {!!permission.canEdit || !!permission.canDelete || !!exportPermission?.canView || (runningJobsCount && runningJobsCount > 0) ? (
                <div className="block h-6 w-0 border-r border-dashed border-gray-300" />
            ) : (
                <></>
            )}
            {!!permission.canEdit && (
                <Tooltip title="Index dataset">
                    <Button
                        type="link"
                        icon={<FontAwesomeIcon icon={faServer} />}
                        disabled={!permission.canEdit}
                        onClick={() => onIndexDataSet(dataSet)}
                        data-testid={CypressTestIds.DATASET_LIST_INDEX_DATASET_DATABASETWOTONE}
                    />
                </Tooltip>
            )}
            {!!exportPermission?.canView && (
                <>
                    <Tooltip title="Export the dataset to a file">
                        <Button
                            type="link"
                            icon={<FontAwesomeIcon icon={faFileExport} />}
                            disabled={!exportPermission.canView}
                            onClick={() => onExportToFileClick && onExportToFileClick(dataSet)}
                            data-testid={CypressTestIds.DATASET_LIST_EXPORT_TO_FILE}
                        />
                    </Tooltip>
                    <Tooltip title="Export the dataset to a destination">
                        <Button
                            type="link"
                            icon={<FontAwesomeIcon icon={faCloudUploadAlt} />}
                            disabled={!exportPermission.canView}
                            onClick={() => onExportToSystemConnectionClick && onExportToSystemConnectionClick(dataSet)}
                            data-testid={CypressTestIds.DATASET_LIST_EXPORT_TO_DESTINATION}
                        />
                    </Tooltip>
                </>
            )}
            {!!runningJobsCount && runningJobsCount > 0 ? (
                <Tooltip placement="topLeft" title="A job is running. DataSet is locked for delete.">
                    <Button
                        type="text"
                        danger
                        icon={<FontAwesomeIcon icon={faLock} />}
                        data-testid={CypressTestIds.DATASET_LIST_DELETE_BUTTON}
                        onClick={(event) => {
                            event && event.stopPropagation && event.stopPropagation();
                            event && event.preventDefault && event.preventDefault();
                        }}
                        className="locked-from-deletion"
                    />
                </Tooltip>
            ) : (
                !!permission.canDelete && (
                    <Tooltip title="Delete DataSet" placement="topLeft">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteFilled />}
                            disabled={!permission.canDelete}
                            onClick={(event) => {
                                event && event.stopPropagation && event.stopPropagation();
                                onDeleteDataSet(dataSet);
                            }}
                            data-testid={CypressTestIds.DATASET_LIST_DELETE_BUTTON}
                        />
                    </Tooltip>
                )
            )}
        </SlideoutMenu>
    );
}
